import { useEffect, useRef, useState } from 'react';
import { Environment } from '~/engine/data/Environment';
import {
    domainEventBus,
    ROI_CONTOUR_EDIT_EVENT_TYPES,
    RoiContourEditEvent,
} from '~/domain/events';

/** Reference-stable comparison, so an unchanged sample re-renders nothing. */
function sameVersions(
    a: ReadonlyMap<number, number>,
    b: ReadonlyMap<number, number>
): boolean {
    if (a.size !== b.size) {
        return false;
    }
    for (const [roiNumber, version] of a) {
        if (b.get(roiNumber) !== version) {
            return false;
        }
    }
    return true;
}

/** The contour version of each of `roiNumbers` in `environment` now. */
function sampleVersions(
    environment: Environment | null,
    roiNumbers: Iterable<number>
): Map<number, number> {
    const versions = new Map<number, number>();
    for (const roiNumber of roiNumbers) {
        versions.set(
            roiNumber,
            environment?.structureSet?.getRoi(roiNumber)?.sdfVersion ?? 0
        );
    }
    return versions;
}

/**
 * Current contour version of each of `roiNumbers`, re-sampled whenever one
 * of them is edited. Missing or not-yet-loaded structures read 0.
 *
 * The version is {@link RegionOfInterest.sdfVersion} — the ROI-level
 * counter kept for exactly this purpose — and NOT the SDF's own
 * `dataVersion`: a replaced texture carries a fresh field whose
 * `dataVersion` restarts at 0, so a reader keyed on it can watch geometry
 * change from version 3 to version 0 and, worse, see an edited structure
 * report the same 0 it reported before (an ABA collision). `sdfVersion`
 * is monotonic across texture swaps.
 *
 * Sampling alone is not enough to keep it current: contour edits mutate
 * the SDF in place and touch no React state, so a consumer that only
 * re-reads on render can hold a version from before an edit indefinitely.
 * The three contour-edit events are therefore observed — strokes publish
 * `RoiContoursModifiedEvent`, while undo and redo restore the pixels
 * through their own events WITHOUT re-emitting it, so watching the first
 * alone goes stale on an undo (see {@link RoiContourEditEvent}).
 *
 * @param environment Engine environment holding the live structure set.
 * @param roiNumbers ROIs to watch. Order is irrelevant; an empty list
 *                   subscribes to nothing.
 */
export function useRoiSdfVersions(
    environment: Environment | null,
    roiNumbers: readonly number[]
): ReadonlyMap<number, number> {
    // Sampled on the first render too, so a consumer keyed on the
    // versions does not see a change from nothing to the first sample.
    const [versions, setVersions] = useState<ReadonlyMap<number, number>>(() =>
        sampleVersions(environment, roiNumbers)
    );

    // Read through a ref so a fresh array of the SAME numbers does not
    // resubscribe on every render; the key below decides when it must.
    const roiNumbersRef = useRef(roiNumbers);
    roiNumbersRef.current = roiNumbers;
    const watchedKey = [...roiNumbers].sort((a, b) => a - b).join(',');

    useEffect(() => {
        const watched = new Set(roiNumbersRef.current);
        const sample = () => {
            const next = sampleVersions(environment, watched);
            setVersions((previous) =>
                sameVersions(previous, next) ? previous : next
            );
        };
        sample();

        const onContourEdit = (event: RoiContourEditEvent) => {
            if (watched.has(event.roiNumber)) {
                sample();
            }
        };
        const subscriptions = ROI_CONTOUR_EDIT_EVENT_TYPES.map((eventType) =>
            domainEventBus.subscribe(eventType, onContourEdit)
        );
        return () => {
            subscriptions.forEach((subscription) => subscription.unsubscribe());
        };
    }, [environment, watchedKey]);

    return versions;
}
